"use client";

import { UserProfile } from "@/types";
import { useState } from "react";
import { User, Activity, Target, Save, ArrowLeft, Trash2, AlertTriangle } from "lucide-react";
import clsx from "clsx";
import { GOAL_CATEGORIES, GOAL_OPTIONS } from "@/data/goals";

interface ProfileScreenProps {
    currentProfile: UserProfile;
    onSave: (profile: UserProfile) => void;
    onBack: () => void;
    onDelete: () => void;
}

export default function ProfileScreen({ currentProfile, onSave, onBack, onDelete }: ProfileScreenProps) {
    const [profile, setProfile] = useState<UserProfile>(currentProfile);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleChange = (field: keyof UserProfile, value: string | number) => {
        setProfile((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <div className="flex flex-col h-full glass rounded-2xl overflow-hidden relative shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-5 bg-white/5 border-b border-white/5 shrink-0">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="p-1 -ml-1 text-text-muted hover:text-white transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <User size={20} className="text-green-500" />
                        Your Profile
                    </h2>
                </div>

            </div>

            {/* Scrollable Form Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-hide relative z-10">

                {/* ID & Contact Info */}
                <div className="space-y-4">
                    <div className="bg-white/5 border border-white/5 rounded-2xl p-4 space-y-4">
                        {/* Name */}
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold block mb-2">
                                Full Name
                            </label>
                            <input
                                type="text"
                                value={profile.name || ""}
                                onChange={(e) => handleChange("name", e.target.value)}
                                className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm focus:outline-none focus:border-green-500/50 transition-colors placeholder:text-white/20"
                                placeholder="Enter your name"
                            />
                        </div>

                        {/* Mobile */}
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold block mb-2">
                                Mobile Number
                            </label>
                            <input
                                type="tel"
                                value={profile.mobile || ""}
                                onChange={(e) => handleChange("mobile", e.target.value)}
                                className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm focus:outline-none focus:border-green-500/50 transition-colors placeholder:text-white/20"
                                placeholder="98765 43210"
                            />
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold block ml-1">
                            Weight (kg)
                        </label>
                        <div className="bg-white/5 border border-white/5 rounded-2xl p-1">
                            <input
                                type="number"
                                value={profile.weight}
                                onChange={(e) => handleChange("weight", parseFloat(e.target.value) || 0)}
                                className="w-full bg-transparent border-none text-center py-3 text-white text-lg font-bold focus:outline-none"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold block ml-1">
                            Height (cm)
                        </label>
                        <div className="bg-white/5 border border-white/5 rounded-2xl p-1">
                            <input
                                type="number"
                                value={profile.height}
                                onChange={(e) => handleChange("height", parseFloat(e.target.value) || 0)}
                                className="w-full bg-transparent border-none text-center py-3 text-white text-lg font-bold focus:outline-none"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold block ml-1">
                            Age
                        </label>
                        <div className="bg-white/5 border border-white/5 rounded-2xl p-1">
                            <input
                                type="number"
                                value={profile.age}
                                onChange={(e) => handleChange("age", parseFloat(e.target.value) || 0)}
                                className="w-full bg-transparent border-none text-center py-3 text-white text-lg font-bold focus:outline-none"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold block ml-1">
                            Gender
                        </label>
                        <div className="grid grid-cols-2 gap-1 bg-white/5 border border-white/5 rounded-2xl p-1 h-[54px]">
                            {["Male", "Female"].map((g) => (
                                <button
                                    key={g}
                                    onClick={() => handleChange("gender", g)}
                                    className={clsx(
                                        "rounded-xl text-xs font-bold transition-all h-full",
                                        profile.gender === g
                                            ? "bg-green-500 text-black shadow-lg shadow-green-500/20"
                                            : "text-text-muted hover:text-white hover:bg-white/5"
                                    )}
                                >
                                    {g}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Activity Level */}
                <div className="space-y-2 pt-2">
                    <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold flex items-center gap-1 mb-2">
                        <Activity size={14} className="text-green-500" /> Activity Level
                    </label>
                    <div className="flex flex-col gap-2">
                        {["Sedentary", "Light", "Moderate", "Active", "Very Active"].map((level) => (
                            <button
                                key={level}
                                onClick={() => handleChange("activityLevel", level)}
                                className={clsx(
                                    "w-full px-4 py-3.5 rounded-xl text-sm font-medium border text-left transition-all",
                                    profile.activityLevel === level
                                        ? "bg-green-500/20 border-green-500 text-green-500 shadow-lg shadow-green-500/10"
                                        : "bg-white/5 border-white/5 text-text-muted hover:bg-white/10 hover:text-white hover:border-white/10"
                                )}
                            >
                                {level}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Goal */}
                <div className="space-y-2 pt-2">
                    <label className="text-[10px] uppercase tracking-wider text-text-muted font-bold flex items-center gap-1 mb-2">
                        <Target size={14} className="text-green-500" /> Current Goal
                    </label>
                    <div className="relative">
                        <select
                            value={profile.goal}
                            onChange={(e) => handleChange("goal", e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-4 text-white text-sm appearance-none focus:outline-none focus:border-green-500/50 transition-colors cursor-pointer"
                        >
                            <option value="" disabled className="bg-slate-900 text-gray-500">Select a goal...</option>
                            {GOAL_CATEGORIES.map(category => (
                                <optgroup key={category} label={category} className="bg-slate-900 text-white">
                                    {GOAL_OPTIONS.filter(g => g.Category === category).map(option => (
                                        <option key={option.Goal} value={option.Goal}>
                                            {option.Goal}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-text-muted">
                            <Target size={18} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/5 bg-black/40 backdrop-blur-md relative z-10">
                <button
                    onClick={() => onSave(profile)}
                    className="w-full bg-green-500 hover:bg-green-400 text-[#020617] rounded-xl py-3.5 text-sm font-bold shadow-lg shadow-green-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                    <Save size={18} />
                    Save & Return
                </button>

                <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full mt-4 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 rounded-xl py-3 text-xs font-bold transition-all flex items-center justify-center gap-2"
                >
                    <Trash2 size={14} />
                    Delete Account
                </button>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#0f172a] border border-white/10 rounded-2xl w-full max-w-sm p-6 shadow-2xl relative overflow-hidden">
                        {/* Background warning tint */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-red-500" />
                        
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 mb-2">
                                <AlertTriangle size={32} />
                            </div>
                            
                            <h3 className="text-xl font-bold text-white">Delete Account?</h3>
                            
                            <p className="text-sm text-text-muted leading-relaxed">
                                This action is <span className="text-red-400 font-bold underline">permanent</span>. All your logs, profile data, and history will be wiped from our servers. 
                            </p>

                            <div className="flex flex-col w-full gap-3 pt-4">
                                <button
                                    onClick={async () => {
                                        setIsDeleting(true);
                                        await onDelete();
                                        setIsDeleting(false);
                                        setShowDeleteConfirm(false);
                                    }}
                                    disabled={isDeleting}
                                    className="w-full bg-red-500 hover:bg-red-400 disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-red-500/20"
                                >
                                    {isDeleting ? "Deleting..." : "Yes, Delete Everything"}
                                </button>
                                
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    disabled={isDeleting}
                                    className="w-full bg-white/5 hover:bg-white/10 text-white font-medium py-3 rounded-xl transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
